<template>
  <div class="qa-search-wrapper" :class="{ 'qa-search-wrapper--open': isOpen }">
    <!-- Collapsed: just a search icon button -->
    <transition name="qa-toggle-fade">
      <button
        v-if="!isOpen"
        class="qa-search-toggle"
        @click="open"
      >
        <v-icon :size="20">{{ toggleIcon }}</v-icon>
      </button>
    </transition>

    <!-- Expanded: input + results -->
    <transition
      name="qa-search-expand"
      @after-leave="onAfterLeave"
    >
      <div v-if="isOpen" class="qa-search-expanded">
        <div class="qa-search-input-row">
          <v-icon :size="20" class="qa-search-icon">mdi-magnify</v-icon>
          <input
            ref="inputRef"
            v-model="query"
            type="text"
            class="qa-search-input"
            :placeholder="placeholder || $t('quickSearch.placeholder')"
            @input="onInput"
            @keydown.escape="close"
          />
          <button class="qa-search-close" @click="close">
            <v-icon :size="18">mdi-close</v-icon>
          </button>
        </div>

      <!-- Results dropdown -->
      <Teleport to="body">
        <div v-if="isOpen && !noDropdown" class="qa-search-overlay" @click="close" />
        <transition name="qa-search-results">
          <div
            v-if="showResults"
            class="qa-search-dropdown"
            :style="dropdownStyle"
          >
            <!-- Loading -->
            <div v-if="loading" class="qa-search-status">
              <v-progress-circular indeterminate size="20" width="2" />
            </div>

            <!-- Empty state -->
            <div v-else-if="query.length >= 2 && results.length === 0 && hasSearched" class="qa-search-status">
              <span class="qa-search-empty-text">{{ emptyText || $t('quickSearch.empty') }}</span>
            </div>

            <!-- Results list -->
            <div
              v-for="item in results"
              :key="item.id"
              class="qa-search-result"
              @click="onSelect(item)"
            >
              <div class="qa-search-result-avatar">
                <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.title" />
                <v-icon v-else size="22" style="opacity: 0.5;">{{ fallbackIcon }}</v-icon>
              </div>
              <div class="qa-search-result-info">
                <div class="qa-search-result-title">{{ item.title }}</div>
                <div v-if="item.subtitle" class="qa-search-result-subtitle">{{ item.subtitle }}</div>
              </div>
              <button
                v-if="item.canEdit"
                class="qa-search-result-menu-btn"
                @click.stop="openResultMenu($event, item)"
              >
                <v-icon size="18">mdi-dots-vertical</v-icon>
              </button>
              <v-icon v-else size="16" class="qa-search-result-chevron">mdi-chevron-right</v-icon>
            </div>

            <!-- "Load more" -->
            <div
              v-if="hasMore && results.length > 0"
              class="qa-search-result qa-search-load-more"
              @click="$emit('load-more')"
            >
              <v-progress-circular v-if="loadingMore" indeterminate size="16" width="2" class="mr-2" />
              <v-icon v-else size="18" class="mr-2" style="opacity: 0.4;">mdi-plus-circle-outline</v-icon>
              <span class="qa-search-empty-text">{{ loadingMore ? $t('quickSearch.loading') : $t('loadMore') }}</span>
            </div>
          </div>
        </transition>

        <!-- Result context menu -->
        <Transition name="fade">
          <div
            v-if="resultMenuOpen"
            class="context-overlay"
            @click="closeResultMenu"
          />
        </Transition>
        <Transition name="context-menu">
          <div
            v-if="resultMenuOpen && resultMenuItem"
            class="context-menu-content-slot"
            :style="resultMenuStyle"
          >
            <slot name="result-menu" :item="resultMenuItem" :close="closeResultMenu" />
          </div>
        </Transition>
      </Teleport>
    </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onUnmounted } from 'vue'

export interface SearchResultItem {
  id: string
  title: string
  subtitle?: string
  imageUrl?: string | null
  canEdit?: boolean
}

const props = withDefaults(defineProps<{
  /** Placeholder text for the input */
  placeholder?: string
  /** Text shown when no results found */
  emptyText?: string
  /** Icon shown when result has no image */
  fallbackIcon?: string
  /** Icon for the collapsed toggle button */
  toggleIcon?: string
  /** When true, no dropdown is shown – the search event is emitted for external handling */
  noDropdown?: boolean
  /** Search results */
  results?: SearchResultItem[]
  /** Whether currently loading */
  loading?: boolean
  /** Whether there are more results to load */
  hasMore?: boolean
  /** Whether loading more results */
  loadingMore?: boolean
}>(), {
  placeholder: '',
  emptyText: '',
  fallbackIcon: 'mdi-shield-outline',
  toggleIcon: 'mdi-magnify',
  noDropdown: false,
  results: () => [],
  loading: false,
  hasMore: false,
  loadingMore: false,
})

const emit = defineEmits<{
  (e: 'search', query: string): void
  (e: 'select', item: SearchResultItem): void
  (e: 'load-more'): void
  (e: 'open'): void
  (e: 'close'): void
  (e: 'menu-action', item: SearchResultItem, action: string): void
}>()

const isOpen = ref(false)
const query = ref('')
const inputRef = ref<HTMLInputElement | null>(null)
const hasSearched = ref(false)
const dropdownStyle = ref<Record<string, string>>({})

let debounceTimer: ReturnType<typeof setTimeout> | null = null

const showResults = computed(() => {
  if (props.noDropdown) return false
  return isOpen.value && query.value.length >= 2 && (props.loading || hasSearched.value)
})

async function open() {
  isOpen.value = true
  emit('open')
  await nextTick()
  inputRef.value?.focus()
  updateDropdownPosition()
}

function close() {
  isOpen.value = false
  query.value = ''
  hasSearched.value = false
  emit('close')
}

function onAfterLeave() {
  // cleanup after collapse animation finishes
}

function updateDropdownPosition() {
  const el = inputRef.value?.closest('.qa-search-expanded')
  if (!el) return
  const rect = el.getBoundingClientRect()
  dropdownStyle.value = {
    top: `${rect.bottom + 6}px`,
    right: `${window.innerWidth - rect.right}px`,
    width: `${Math.max(rect.width, 280)}px`,
  }
}

function onInput() {
  if (debounceTimer) clearTimeout(debounceTimer)
  if (query.value.length < 3) {
    hasSearched.value = false
    emit('search', '')
    return
  }
  debounceTimer = setTimeout(() => {
    hasSearched.value = true
    emit('search', query.value)
    updateDropdownPosition()
  }, 300)
}

function onSelect(item: SearchResultItem) {
  emit('select', item)
  close()
}

// --- Result context menu ---
const resultMenuOpen = ref(false)
const resultMenuItem = ref<SearchResultItem | null>(null)
const resultMenuStyle = ref<Record<string, string>>({})

function openResultMenu(event: MouseEvent, item: SearchResultItem) {
  const btn = event.currentTarget as HTMLElement
  const rect = btn.getBoundingClientRect()
  resultMenuStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 6}px`,
    right: `${window.innerWidth - rect.right}px`,
    zIndex: '9999',
  }
  resultMenuItem.value = item
  resultMenuOpen.value = true
}

function closeResultMenu() {
  resultMenuOpen.value = false
  resultMenuItem.value = null
}

onUnmounted(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
})

defineExpose({ open, close, isOpen })
</script>

<style lang="scss" scoped>
.qa-search-wrapper {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
}

.qa-search-toggle {
  width: 42px;
  height: 42px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(var(--v-theme-sheetText), 0.9);
  transition: all 0.2s ease;
  border-radius: 8px;
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.9);
    background: rgba(var(--v-theme-sheetText), 0.1);
  }
}

.qa-search-expanded {
  display: flex;
  align-items: center;
  min-width: 140px;
  max-width: 280px;
  overflow: hidden;
  flex: 1 1 auto;
}

/* --- Expand / Collapse transition --- */
.qa-search-expand-enter-active {
  transition: max-width 0.4s cubic-bezier(0.22, 1, 0.36, 1),
              min-width 0.4s cubic-bezier(0.22, 1, 0.36, 1),
              opacity 0.3s ease 0.1s;
}
.qa-search-expand-leave-active {
  transition: max-width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
              min-width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.2s ease;
}
.qa-search-expand-enter-from,
.qa-search-expand-leave-to {
  max-width: 42px;
  min-width: 42px;
  opacity: 0;
  flex: 0 0 42px;
}

/* --- Toggle button fade (icon button) --- */
.qa-toggle-fade-enter-active {
  transition: opacity 0.25s ease 0.2s;
}
.qa-toggle-fade-leave-active {
  transition: opacity 0.15s ease;
}
.qa-toggle-fade-enter-from,
.qa-toggle-fade-leave-to {
  opacity: 0;
}

.qa-search-input-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 4px 8px;
  border-radius: 20px;
  background: rgba(var(--v-theme-sheetText), 0.08);

  .v-theme--light & {
    background: rgba(0, 0, 0, 0.06);
  }
}

.qa-search-icon {
  flex-shrink: 0;
  opacity: 0.5;
  color: rgba(var(--v-theme-sheetText), 0.7);
}

.qa-search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: rgb(var(--v-theme-sheetText));
  min-width: 0;

  &::placeholder {
    color: rgba(var(--v-theme-sheetText), 0.4);
  }
}

.qa-search-close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(var(--v-theme-sheetText), 0.5);
  border-radius: 50%;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background: rgba(var(--v-theme-sheetText), 0.1);
  }
}
</style>

<style lang="scss">
/* Global (not scoped) — Teleported elements */
.qa-search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  z-index: 9988;
}

.qa-search-dropdown {
  position: fixed;
  max-height: 360px;
  overflow-y: auto;
  background: rgba(var(--v-theme-sheetSection), 0.85);
  backdrop-filter: blur(60px) saturate(180%);
  -webkit-backdrop-filter: blur(60px) saturate(180%);
  border-radius: 14px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.35);
  z-index: 9992;
  padding: 6px 0;

  .v-theme--light & {
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  }
}

.qa-search-status {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.qa-search-empty-text {
  font-size: 14px;
  color: rgba(var(--v-theme-sheetText), 0.5);
}

.qa-search-result {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s ease;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background: rgba(var(--v-theme-sheetText), 0.08);
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.06);
  }
}

.qa-search-result-avatar {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-sheetText), 0.06);
  margin-right: 12px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.qa-search-result-info {
  flex: 1;
  min-width: 0;
}

.qa-search-result-title {
  font-size: 15px;
  font-weight: 500;
  color: rgb(var(--v-theme-sheetText));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qa-search-result-subtitle {
  font-size: 12px;
  color: rgba(var(--v-theme-sheetText), 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
}

.qa-search-result-chevron {
  flex-shrink: 0;
  opacity: 0.3;
  margin-left: 8px;
}

.qa-search-load-more {
  justify-content: center;
}

.qa-search-result-menu-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(var(--v-theme-sheetText), 0.4);
  border-radius: 50%;
  margin-left: 4px;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease;

  &:active {
    background: rgba(var(--v-theme-sheetText), 0.1);
  }
}

/* Transition */
.qa-search-results-enter-active {
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.qa-search-results-leave-active {
  transition: all 0.15s ease;
}
.qa-search-results-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}
.qa-search-results-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}
</style>
